import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Button, Text, TextField } from '@cinesend/ui-kit'
import { ArrowCounterClockwise, ArrowsClockwise, Image as ImageIcon, Trash } from '@phosphor-icons/react'
import AppDialog from '../app_dialog'
import ImageModal from './image_modal'
import { useConfirm } from '../../contexts/confirm_dialog'
import { humanFileSize } from '../../helpers/human_file_size'
import isEmpty from '../../helpers/is_empty'
import useStore from '../../hooks/use_store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export default function ImageField({ assetID, field, value, message, saveField, refreshAsset, cacheKey }) {
  const label = field.name
  const { cycleID } = useParams()
  const confirm = useConfirm()
  const { deleteModel, updateModel } = useStore()
  const [showImageModal, setShowImageModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [fileName, setFileName] = useState(value ? value : '')

  const assetImage = field.asset_image
  const missingAssetImage = assetImage && !assetImage.id
  const hasMatchedImage = Boolean(assetImage?.id)

  const queryClient = useQueryClient()

  const { data: previewData } = useQuery({
    queryKey: [`/asset-images/${assetImage?.id}`],
    enabled: Boolean(assetImage?.id),
    retry: false,
    refetchOnWindowFocus: false,
  })
  const previewUrl = previewData?.asset_image?.signed_url

  useQuery({
    queryKey: [`field-values/${field.value?.id}`],
    refetchInterval: field?.value?.image_matching ? 2000 : false,
    enabled: field?.value?.image_matching === true,
    onSuccess: (data) => {
      queryClient.setQueryData(cacheKey, (oldData) => {
        if (!oldData) return oldData
        return oldData.map((item) => {
          if (item.id === data.field_id) {
            return { ...item, value: data, asset_image: data.asset_image }
          }
          return item
        })
      })
      if (data.name === 'Poster' && data.asset_image) {
        // If it's a poster and there's an image, also invalidate the standard query for posters
        const standardKey = cacheKey.concat('&isStandard=true')
        queryClient.invalidateQueries(standardKey)
      }
    },
  })

  useEffect(() => {
    if (value !== fileName && assetImage) {
      setFileName(value)
    }
  }, [value, fileName, setFileName, assetImage])

  const saveImageField = (newValue) => {
    // If field already has a value, don't overwrite it, it was set in csv (probably) to validate
    // that the correct asset is being manually selected on this form.
    // If its currently empty though, we should set a value so it has something.
    if (!isEmpty(fileName)) {
      return
    }
    refreshAsset()
    saveField(newValue ?? '')
  }

  const clearMatchedImage = () => {
    setFileName('')
    saveField('')
    refreshAsset()
    queryClient.invalidateQueries([`field-values`])
  }

  const unmatchImage = async () => {
    const accepted = await confirm({
      title: 'Unmatch image',
      message: `Unmatches “${assetImage.file_name}” from this field. The image file stays available to rematch.`,
      confirmText: 'Unmatch image',
      confirmVariant: 'destructive',
    })
    if (!accepted) return

    updateModel.mutate(
      {
        endpoint: '/asset-images',
        id: assetImage.id,
        data: {
          asset_id: null,
          field_id: null,
          cycle_id: cycleID,
          status: 'uploaded',
        },
      },
      {
        onSuccess: clearMatchedImage,
      }
    )
  }

  const deleteImage = () => {
    deleteModel.mutate(
      {
        endpoint: 'asset-images',
        id: assetImage.id,
        params: `?cycleID=${cycleID}&assetID=${assetID}`,
        deleteNoun: 'image',
        deleteName: assetImage.file_name,
      },
      {
        onSuccess: clearMatchedImage,
      }
    )
  }

  return (
    <div className='flex w-full flex-col gap-3'>
      <div className='flex w-full items-start gap-4'>
        {previewUrl ? (
          <button
            type='button'
            onClick={() => setShowPreviewModal(true)}
            aria-label={`View larger ${assetImage?.file_name || label}`}
            className='flex h-28 w-28 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-[color:var(--cs-color-border-default)] bg-[var(--cs-color-background-secondary)]'
          >
            <img
              src={previewUrl}
              alt={assetImage?.file_name || label}
              className='max-h-full max-w-full object-contain'
            />
          </button>
        ) : (
          <div className='flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[color:var(--cs-color-border-default)] bg-[var(--cs-color-background-secondary)]'>
            <div className='flex flex-col items-center gap-1 px-2 text-center'>
              <ImageIcon
                size={28}
                weight='regular'
                aria-hidden='true'
                className='text-[color:var(--cs-color-foreground-secondary)]'
              />
              <Text variant='xs' tone='secondary'>
                No preview
              </Text>
            </div>
          </div>
        )}
        {showPreviewModal && previewUrl ? (
          <AppDialog title={assetImage?.file_name || label} onClose={() => setShowPreviewModal(false)} size='lg'>
            <img
              src={previewUrl}
              alt={assetImage?.file_name || label}
              className='mx-auto max-h-[70vh] w-full object-contain'
            />
          </AppDialog>
        ) : null}
        <div className='min-w-0 flex-1 space-y-1'>
          <div className='flex items-start space-x-2'>
            <TextField
              label={field.is_required ? `${label} *` : label}
              error={message || (field.is_required && !fileName ? 'Required' : undefined)}
              placeholder={'Enter a file name to match automatically to bulk uploaded images'}
              value={fileName ?? ''}
              disabled={!!assetImage || field?.value?.image_matching}
              onChange={(e) => setFileName(e.target.value)}
              onBlur={(e) => saveField(e.target.value)} // use standard `saveField` here so value can be manually edited
            />
            {hasMatchedImage ? (
              <TextField rootClassName='max-w-24' label={'File size'} disabled value={humanFileSize(assetImage.size)} />
            ) : null}
            {hasMatchedImage ? (
              <div className='mt-[30px] flex shrink-0 items-center gap-1'>
                <Button
                  variant='ghost'
                  iconOnly
                  tooltip='Unmatch'
                  disabled={field?.value?.image_matching || updateModel.isLoading}
                  leadingIcon={<ArrowCounterClockwise size={20} weight='regular' aria-hidden='true' />}
                  onClick={unmatchImage}
                >
                  Unmatch
                </Button>
                <Button
                  variant='ghost'
                  iconOnly
                  tooltip='Delete'
                  disabled={field?.value?.image_matching || deleteModel.isLoading}
                  leadingIcon={<Trash size={20} weight='regular' aria-hidden='true' />}
                  onClick={deleteImage}
                >
                  Delete
                </Button>
              </div>
            ) : (
              <Button
                disabled={field?.value?.image_matching}
                className='mt-[30px] min-w-40'
                variant='secondary'
                onClick={() => setShowImageModal(true)}
              >
                Select image
              </Button>
            )}
            {showImageModal ? (
              <ImageModal
                onClose={() => setShowImageModal(false)}
                assetImage={assetImage}
                assetID={assetID}
                field={field}
                refrehAsset={refreshAsset}
                saveField={saveImageField}
              />
            ) : null}
          </div>
          {field?.value?.image_matching && (
            <Alert tone='info' size='sm' icon={<ArrowsClockwise size={20} className='animate-spin' />}>
              Image matching in progress...
            </Alert>
          )}
          {missingAssetImage && (
            <Alert tone='error' size='sm'>
              No image found for this asset. Please upload an image.
            </Alert>
          )}
        </div>
      </div>
    </div>
  )
}
